import React, { useContext, ReactNode } from "react";
import { PrincipalsCollection } from "areas/account/models/Principal";
import { PrincipalRefreshProvider } from "areas/account/components/PrincipalRefreshProvider";
import {
  PrincipalProviderMachine,
  PrincipalProviderMachineEvents,
  PrincipalProviderMachineStates,
} from "areas/account/components/PrincipalProviderMachine";
import { useMachine } from "@xstate/react";
import Error, { ErrorCodes } from "areas/layout/components/Error";

export const PrincipalContext = React.createContext<PrincipalsCollection>(
  new PrincipalsCollection()
);

export const usePrincipalContext = () => useContext(PrincipalContext);

interface PrincipalProviderProps {
  children: ReactNode[] | ReactNode;
}

export const PrincipalProvider = ({ children }: PrincipalProviderProps) => {
  const [machineState, sendMachineEvent] = useMachine(PrincipalProviderMachine);
  //console.debug(`PrincipalProvider: machineState = `, machineState);

  const handleRefreshPrincipals = () => {
    sendMachineEvent({ type: PrincipalProviderMachineEvents.Refetch });
  };

  return (
    <>
      {machineState.matches(PrincipalProviderMachineStates.Fetched) && (
        <PrincipalContext.Provider
          value={machineState.context.principals ?? new PrincipalsCollection()}
        >
          <PrincipalRefreshProvider refreshPrincipal={handleRefreshPrincipals}>
            {children}
          </PrincipalRefreshProvider>
        </PrincipalContext.Provider>
      )}
      {machineState.matches(PrincipalProviderMachineStates.Fetching) && (
        // The following 'Loading...' won't be seen by the user because it is 'underneath' the full screen loading screen in index.html.
        // The PrincipalProviderMachine closes the full screen loading screen once principal is loaded by calling closeLoadingScreen().
        // This 'Loading...' here just in case a bug creeps in closing the full screen loading screen early.
        <div>Loading...</div>
      )}
      {machineState.matches(PrincipalProviderMachineStates.FetchError) && (
        <Error errorCode={ErrorCodes.GeneralError} alreadyLogged={true} />
      )}
    </>
  );
};
